import { Component, EventEmitter, OnInit, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { collections } from '@shared/constants/collections';
import { constants } from '@shared/constants/constants';
import { Choice, Offer, Profile, Summary, AdvertisementSource } from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { SettingsService } from '@shared/webapi/services/settings.service';
import { LabelTextPipe } from '@shared/pipes/label-text.pipe';
import { AdvertisementSourceDataModel, Sources } from '@signature/signature.extentions';
import { DropDownFormControl } from '@shared/form/dropdown-form-control/dropdown-form-control';
import { NameWithDigitsFormControl } from '@shared/form/name-with-digits-form-control/name-with-digits-form-control';
import { NameFormControl } from '@form/name-form-control/name-form-control';
import { NumberFormControl } from '@form/number-form-control/number-form-control';
import { cleanObj } from '@shared/extensions/extensions';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ClientFacade } from '../../../../store/client-store/client.facade';
import { ClientType } from '../../../../store/client-store/client.models';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-advertisement-source-client',
  templateUrl: './advertisement-source-client.component.html',
  styleUrls: ['./advertisement-source-client.component.scss']
})
export class AdvertisementSourceClientComponent implements OnInit {
  private readonly settingsService = inject(SettingsService);
  private readonly customerService = inject(CustomerService);
  private readonly labelTextPipe = inject(LabelTextPipe);
  private readonly clientFacade = inject(ClientFacade);

  readonly constants = constants;
  mifForm: FormGroup | null = null;
  summaryConfiguration: Summary | null = null;
  manualOverrideOffer = new EventEmitter<Offer>();
  offers: Offer[] = [];
  offer: Offer | null = null;
  clientProfile: Profile | null = null;
  clientProfileForViewOffer: Profile | null = null;
  clientProfileAdvertisementSourceOrig: AdvertisementSource | null = null;
  advertisementSourceCodeCollection: Choice[] = [];
  advertisementSourceCodeControl: DropDownFormControl | null = null;
  promoCodeControl: NameWithDigitsFormControl | null = null;
  partnerCodeControl: NameWithDigitsFormControl | null = null;
  sponsorNumberControl: NumberFormControl | null = null;
  memberNumberControl: NumberFormControl | null = null;
  advSourceFirstNameControl: NameFormControl | null = null;
  advSourceLastNameControl: NameFormControl | null = null;
  mifAdvisorCodeControl: NumberFormControl | null = null;
  source: string | null = null;
  readonly sources = Sources;
  private clientType: ClientType | null = null;
  private prevMifFormData: AdvertisementSourceDataModel | null = null;
  private pendingProspectAdvertisementSourceModel: AdvertisementSourceDataModel | null = null;

  async ngOnInit(): Promise<void> {
    this.summaryConfiguration = await this.settingsService.getSummaryConfiguration();
    this.initForm();
    this.initControls();
    this.initSubscriptions();
    this.restoreAnswersOnForm();
    this.initAdvertisementSourceCodeCollection();
  }

  get isContract(): boolean {
    return this.clientType === ClientType.Contract;
  }

  get isMember(): boolean {
    return this.isContract;
  }

  get isMifFormValid(): boolean {
    return !!this.mifForm?.valid;
  }

  async saveAdvertisementSourceData(): Promise<string | null> {
    if (!this.mifForm || this.mifForm.invalid) {
      this.mifForm?.markAllAsTouched();
      return null;
    }

    return this.customerService.updateAdvertisementSourceDataModel(
      this.mifForm.getRawValue() as AdvertisementSourceDataModel
    );
  }

  markAllAsTouched(): void {
    this.mifForm?.markAllAsTouched();
  }

  onOffersItem(item: Offer): void {
    const objForComparation = cleanObj(Object.assign({}, this.clientProfile?.advertisementSource));

    if (
      this.clientProfile?.advertisementSource === undefined ||
      this.clientProfile?.advertisementSource?.advertisementSourceCode === undefined ||
      JSON.stringify(objForComparation) !== JSON.stringify(this.clientProfileAdvertisementSourceOrig)
    ) {
      this.offer = item;

      if (item) {
        this.promoCodeControl?.patchValue(item.promoCode);
      } else {
        this.promoCodeControl?.patchValue(null);
      }
    }
  }

  onOffersItems(items: Offer[]): void {
    this.offers = items;
    this.restorePromoCode();
  }

  onSourceChange(event: { value: string }, firstTime: boolean = false): void {
    this.source = event.value;

    if (!firstTime) {
      this.sponsorNumberControl?.patchValue(null);
      this.partnerCodeControl?.patchValue(null);
      this.memberNumberControl?.patchValue(null);
      this.advSourceFirstNameControl?.patchValue(null);
      this.advSourceLastNameControl?.patchValue(null);
    }

    switch (this.source) {
      case Sources.PARRAINAGE:
        this.sponsorNumberControl?.markAsRequred();
        this.memberNumberControl?.markAsNotRequred();
        this.partnerCodeControl?.markAsNotRequred();
        this.advSourceFirstNameControl?.markAsNotRequred();
        this.advSourceLastNameControl?.markAsNotRequred();
        break;
      case Sources.PARTENAIRE:
        this.partnerCodeControl?.markAsRequred();
        this.sponsorNumberControl?.markAsNotRequred();
        this.memberNumberControl?.markAsNotRequred();
        this.advSourceFirstNameControl?.markAsNotRequred();
        this.advSourceLastNameControl?.markAsNotRequred();
        break;
      case Sources.MEMBRE_CORRESPONDANT:
        this.memberNumberControl?.markAsRequred();
        this.sponsorNumberControl?.markAsNotRequred();
        this.partnerCodeControl?.markAsNotRequred();

        if (this.memberNumberControl?.value) {
          this.advSourceFirstNameControl?.markAsNotRequred();
          this.advSourceLastNameControl?.markAsNotRequred();
        } else {
          this.advSourceFirstNameControl?.markAsRequred();
          this.advSourceLastNameControl?.markAsRequred();
        }
        break;
      default:
        this.sponsorNumberControl?.markAsNotRequred();
        this.partnerCodeControl?.markAsNotRequred();
        this.memberNumberControl?.markAsNotRequred();
        this.advSourceFirstNameControl?.markAsNotRequred();
        this.advSourceLastNameControl?.markAsNotRequred();
        this.sponsorNumberControl?.patchValue(null);
        this.partnerCodeControl?.patchValue(null);
        this.memberNumberControl?.patchValue(null);
        this.advSourceFirstNameControl?.patchValue(null);
        this.advSourceLastNameControl?.patchValue(null);
        break;
    }
  }

  private initForm(): void {
    this.mifForm = new FormGroup({
      advertisementSourceCode: new DropDownFormControl(false, false, {
        collection: this.advertisementSourceCodeCollection,
        valuePropertyName: 'label',
        keyPropertyName: 'code'
      }),
      promoCode: new NameWithDigitsFormControl(false, false, {
        hideErrorMessages: false,
        incorrectErrorMessage: collections.messages.promoCodeIncorrectMessage
      }),
      partnerCode: new NameWithDigitsFormControl(false, false),
      sponsorNumber: new NumberFormControl(false, false, 8, 8, true, {
        min: 1000000,
        minValueErrorMessage: collections.messages.sponsorNumberError,
        requiredErrorMessage: collections.messages.sponsorNumberError
      }),
      memberNumber: new NumberFormControl(false, false, undefined, 3, true, {
        requiredErrorMessage: collections.messages.memberNumberError
      }),
      firstName: new NameFormControl(false, false, {
        capitalizeAll: true,
        hideSuccessMark: false,
        hideErrorMessages: true,
        maxLength: constants.maxLength.firstName
      }),
      lastName: new NameFormControl(false, false, {
        capitalizeAll: true,
        hideSuccessMark: true,
        hideErrorMessages: true,
        maxLength: constants.maxLength.lastName
      }),
      mifAdvisorCode: new NumberFormControl(false, false, 4, 4, true, {
        min: 1000,
        minValueErrorMessage: collections.messages.mifLorsCodeError,
        requiredErrorMessage: collections.messages.mifLorsCodeError
      })
    });
  }

  private initControls(): void {
    this.advertisementSourceCodeControl = this.mifForm?.get('advertisementSourceCode') as DropDownFormControl | null;
    this.promoCodeControl = this.mifForm?.get('promoCode') as NameWithDigitsFormControl | null;
    this.partnerCodeControl = this.mifForm?.get('partnerCode') as NameWithDigitsFormControl | null;
    this.sponsorNumberControl = this.mifForm?.get('sponsorNumber') as NumberFormControl | null;
    this.memberNumberControl = this.mifForm?.get('memberNumber') as NumberFormControl | null;
    this.advSourceFirstNameControl = this.mifForm?.get('firstName') as NameFormControl | null;
    this.advSourceLastNameControl = this.mifForm?.get('lastName') as NameFormControl | null;
    this.mifAdvisorCodeControl = this.mifForm?.get('mifAdvisorCode') as NumberFormControl | null;
  }

  private initSubscriptions(): void {
    this.clientFacade.clientType$.pipe(untilDestroyed(this)).subscribe(clientType => {
      this.clientType = clientType;
      this.initAdvertisementSourceCodeCollection();
    });

    this.clientFacade.pendingProspectAdvertisementSource$
      .pipe(untilDestroyed(this))
      .subscribe((advertisementSourceModel: AdvertisementSourceDataModel | null) => {
        this.pendingProspectAdvertisementSourceModel = advertisementSourceModel;

        if (!advertisementSourceModel || this.clientType !== ClientType.Prospect || !this.mifForm) {
          return;
        }

        this.restoreAdvertisementSourceData(advertisementSourceModel);
      });

    this.clientFacade.authorizedClientProfile$.pipe(untilDestroyed(this)).subscribe(profile => {
      if (!profile) {
        return;
      }

      this.setClientProfile(profile);
    });

    this.mifForm?.valueChanges.pipe(debounceTime(50), distinctUntilChanged(), untilDestroyed(this)).subscribe(data => {
      if (this.prevMifFormData?.promoCode !== data.promoCode) {
        this.prevMifFormData = Object.assign({}, data);
        return;
      }

      if (JSON.stringify(this.prevMifFormData) !== JSON.stringify(data)) {
        if (this.prevMifFormData?.promoCode && this.prevMifFormData?.promoCode !== data.promoCode) {
          this.prevMifFormData = Object.assign({}, data);
          return;
        }

        this.prevMifFormData = Object.assign({}, data);

        if (data.advertisementSourceCode === Sources.MEMBRE_CORRESPONDANT) {
          this.mifAdvisorCodeControl?.markAsNotRequred();

          if (data.memberNumber) {
            this.advSourceFirstNameControl?.markAsNotRequred();
            this.advSourceFirstNameControl?.markAsTouched();
            this.advSourceLastNameControl?.markAsNotRequred();
            this.advSourceLastNameControl?.markAsTouched();
          } else {
            this.advSourceFirstNameControl?.markAsRequred();
            this.advSourceLastNameControl?.markAsRequred();
          }

          if (this.advSourceFirstNameControl?.value || this.advSourceLastNameControl?.value) {
            this.memberNumberControl?.markAsNotRequred();
          } else {
            this.memberNumberControl?.markAsRequred();
          }

          if (!data.memberNumber && !data.firstName && !data.lastName) {
            this.advSourceFirstNameControl?.markAsRequred();
            this.advSourceLastNameControl?.markAsRequred();
            this.memberNumberControl?.markAsRequred();
          }
        }

        if (data.mifAdvisorCode) {
          this.mifAdvisorCodeControl?.markAsRequred();
        } else {
          this.mifAdvisorCodeControl?.markAsNotRequred();
        }

        const advertisementSourceModel = this.mifForm?.getRawValue() as AdvertisementSourceDataModel;
        this.clientProfileForViewOffer = this.clientProfile
          ? this.customerService.updateAdvertisementSourceDataModelWithoutSaving(
              this.clientProfile,
              advertisementSourceModel
            )
          : null;
      }
    });

    this.promoCodeControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((promoCodeValue: string) => {
      const offerWithCode = this.offers?.find((offer: Offer) => offer.promoCode === promoCodeValue);

      if (promoCodeValue === null || promoCodeValue?.trim() === '') {
        this.promoCodeControl?.markAsValid();
        this.mifForm?.updateValueAndValidity();
        return;
      }

      this.promoCodeControl?.markValidOrNot(!!offerWithCode);
      this.mifForm?.updateValueAndValidity();

      if (offerWithCode) {
        this.manualOverrideOffer.emit(offerWithCode);
      }
    });
  }

  private restoreAnswersOnForm(): void {
    const profile = this.customerService.userData ? Profile.fromJS(this.customerService.userData) : null;

    if (profile) {
      this.setClientProfile(profile);
    }

    this.restoreAdvertisementSourceData();
  }

  private restoreAdvertisementSourceData(
    model: AdvertisementSourceDataModel | null = this.getAdvertisementSourceDataModel()
  ): void {
    if (!model || !this.mifForm) {
      return;
    }

    this.mifForm.patchValue(model);
    this.onSourceChange({ value: model.advertisementSourceCode || '' }, true);

    const offerWithCode = this.offers?.find((offer: Offer) => offer.promoCode === model.promoCode);

    if (model.promoCode && !!offerWithCode) {
      this.promoCodeControl?.patchValue(model.promoCode);
    } else {
      this.promoCodeControl?.patchValue(null);

      if (this.offer) {
        this.promoCodeControl?.patchValue(this.offer.promoCode);
      }
    }
  }

  private restorePromoCode(): void {
    const model = this.getAdvertisementSourceDataModel();

    if (!model) {
      return;
    }

    const offerWithCode = this.offers?.find((offer: Offer) => offer.promoCode === model.promoCode);

    if (model.promoCode && !!offerWithCode) {
      this.promoCodeControl?.patchValue(model.promoCode);
    } else {
      this.promoCodeControl?.patchValue(null);

      if (this.offer) {
        this.promoCodeControl?.patchValue(this.offer.promoCode);
      }
    }
  }

  private initAdvertisementSourceCodeCollection(): void {
    const selectedAdvertisementSourceCode = this.getAdvertisementSourceDataModel()?.advertisementSourceCode;

    this.advertisementSourceCodeCollection =
      this.summaryConfiguration?.possibleChoices
        ?.filter((item: Choice) => {
          if (this.isMember) {
            return item.code !== constants.sponsorshipCodeLabelKey;
          }

          return item;
        })
        .map((item: Choice) => {
          const itemWithTranslatedLabel = Object.create(item);
          itemWithTranslatedLabel.label = this.labelTextPipe.transform(`SUMMARY.${item.label}`);
          return itemWithTranslatedLabel;
        }) ?? [];

    this.advertisementSourceCodeControl?.setCollection(this.advertisementSourceCodeCollection);

    const hasSelectedAdvertisementSourceCode = this.advertisementSourceCodeCollection.some(
      item => item.code === selectedAdvertisementSourceCode
    );

    this.advertisementSourceCodeControl?.patchValue(
      hasSelectedAdvertisementSourceCode ? selectedAdvertisementSourceCode : null
    );

    if (hasSelectedAdvertisementSourceCode) {
      this.onSourceChange({ value: selectedAdvertisementSourceCode || '' }, true);
    }
  }

  private getAdvertisementSourceDataModel(): AdvertisementSourceDataModel | null {
    if (this.clientType === ClientType.Prospect && this.pendingProspectAdvertisementSourceModel) {
      return this.pendingProspectAdvertisementSourceModel;
    }

    return this.customerService.getAdvertisementSourceDataModel();
  }

  private setClientProfile(profile: Profile): void {
    this.clientProfile = Profile.fromJS(profile);
    this.clientProfileForViewOffer = Object.assign({}, this.clientProfile);
    this.clientProfileAdvertisementSourceOrig = cleanObj(
      Object.assign({}, this.clientProfileForViewOffer.advertisementSource)
    );
  }
}
